import {
    faCheckCircle,
    faEnvelope,
    faIdCard,
    faLink,
    faUser,
    faWarning
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { Badge, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Banderas from './Banderas';

/**
 * Componente para mostrar un cliente individual como tarjeta
 */
const ClienteCard = ({ cliente, isSelected = false, onToggleSelection }) => {
    const url = `/clientes/detalle/${cliente.idClienteAkasia}`;
    const noTieneLicencias = cliente.dACL === 0 && cliente.dPPL === 0;
    const soloLicenciasPrueba = cliente.dACL === 0 && cliente.dPPL > 0;
    const tieneLeadVinculado = cliente.bTieneLeadVinculado > 0;

    

    const handleCardClick = (e) => {
        // No activar selección si se hace clic en un botón o enlace
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }

        if (onToggleSelection) {
            onToggleSelection(cliente.idClienteAkasia);
        }
    };

    // Determinar clases CSS según estado
    const cardClasses = [
        'mb-3',
        'shadow-sm',
        noTieneLicencias ? 'border-warning' : '',
        isSelected ? 'border-primary border-3' : ''
    ].filter(Boolean).join(' ');

    // Estilos dinámicos para el fondo compatible con modo oscuro
    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ...(isSelected && {
            backgroundColor: 'rgba(13, 110, 253, 0.1)',
            boxShadow: '0 0 0 0.2rem rgba(13, 110, 253, 0.25)'
        })
    };

    return (
        <Card
            className={cardClasses}
            onClick={handleCardClick}
            style={cardStyle}
        >
            <Card.Body>
                <Row className="align-items-center">
                    {/* ID, Lead y Banderas */}
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                        <div className="d-flex flex-column align-items-start">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                {isSelected && (
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-primary"
                                        size="lg"
                                    />
                                )}
                                <Link to={url} className="text-decoration-none">
                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#2c7be5' }}>
                                        #{cliente.idClienteAkasia}
                                    </span>
                                </Link>
                                {noTieneLicencias && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                Este cliente no tiene ninguna licencia vinculada
                                            </Tooltip>
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faWarning}
                                            className="text-warning"
                                        />
                                    </OverlayTrigger>
                                )}
                            </div>

                            {/* Lead Badge */}
                            {tieneLeadVinculado && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Cliente tiene lead(s) vinculado(s)</Tooltip>}
                                >
                                    <Badge bg="info" className="mb-2 fs--2">
                                        <FontAwesomeIcon icon={faLink} className="me-1" />
                                        Lead
                                    </Badge>
                                </OverlayTrigger>
                            )}
                           
                            {/* Bandera del país */}
                            <div className="mb-2">
                                <Banderas Height={32} vPais={cliente.vPais} />
                            </div>

                            <small className="text-muted">{cliente.joinDate}</small>
                            
                            {/* Estado general */}
                            <div className="small text-end mb-2">
                                {/* Separador */}
                                <hr className="my-2" />
                                {noTieneLicencias ? (
                                    <span className="text-warning">
                                        <FontAwesomeIcon icon={faWarning} className="me-1" />
                                        Sin licencias
                                    </span>
                                ) : soloLicenciasPrueba ? (
                                    <span className="text-danger">
                                        <FontAwesomeIcon icon={faWarning} className="me-1" />
                                        Solo prueba
                                    </span>
                                ) : (
                                    <span className="text-success">
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                        Cliente activo
                                    </span>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* Información principal del cliente */}
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <Row>
                            {/* Columna 1: Datos del Cliente */}
                            <Col xs={12} lg={6} className="mb-2 mb-lg-0">
                                <div className="mb-2">
                                    <h6 className={`mb-2 ${soloLicenciasPrueba ? 'text-danger' : ''}`}>
                                        <Link to={url} className="text-decoration-none">
                                            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                                            <span className={`text-truncate d-inline-block ${soloLicenciasPrueba ? 'text-danger' : ''}`} style={{ maxWidth: '250px' }}>
                                                {cliente.vNombre}
                                            </span>
                                        </Link>
                                    </h6>

                                    <div className="text-muted small mb-1">
                                        <strong className="text-primary">Datos del Cliente</strong>
                                    </div>

                                    {/* Email */}
                                    {cliente.vEmail && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faEnvelope} className="me-1 text-success" />
                                            <a href={`mailto:${cliente.vEmail}`} className="text-decoration-none">
                                                <span className="d-inline-block text-truncate" style={{ maxWidth: '200px' }}>
                                                    {cliente.vEmail}
                                                </span>
                                            </a>
                                        </div>
                                    )}

                                    {/* Teléfono */}
                                    {cliente.vTelefonoPersonal && (
                                        <div className="small mb-1 text-muted">
                                            <a href={`https://wa.me/${cliente.vTelefonoPersonal}`} target="_blank" rel="noreferrer" className="text-decoration-none">
                                                <img src="/img/whatsapp20.png" alt="WhatsApp" className="me-1" />
                                                {cliente.vTelefonoPersonal}
                                            </a>
                                        </div>
                                    )}

                                    {/* País */}
                                    <div className="small text-muted mb-1">
                                        <strong>País:</strong> {cliente.vPais}
                                    </div>

                                    {/* Estado/Departamento/Provincia */}
                                    {cliente.vEstadoDepProv && (
                                        <div className="small text-muted">
                                            <strong>Estado:</strong> {cliente.vEstadoDepProv}
                                        </div>
                                    )}
                                </div>
                            </Col>

                            {/* Columna 2: Datos de Facturación */}
                            <Col xs={12} lg={6}>
                                <div className="mb-2">
                                    <div className="text-muted small mb-2">
                                        <strong className="text-info">Datos de Facturación</strong>
                                    </div>

                                    {/* RFC */}
                                    <div className="small mb-1 text-muted">
                                        <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                        <strong>RFC:</strong> {cliente.vRfc || 'N/A'}
                                    </div>

                                    {/* Razón Social */}
                                    {cliente.vRazonSocial && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                            <strong>Razón Social:</strong>
                                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                                {cliente.vRazonSocial}
                                            </div>
                                        </div>
                                    )}

                                    {/* Número de Registro Tributario */}
                                    {cliente.vNumRegTrib && (
                                        <div className="small mb-1 text-muted">
                                            <FontAwesomeIcon icon={faIdCard} className="me-1 text-info" />
                                            <strong>Núm. Reg. Trib.:</strong> {cliente.vNumRegTrib}
                                        </div>
                                    )}

                                    {/* País de Facturación */}
                                    {cliente.vPaisFacturacion && (
                                        <div className="small text-muted mb-1">
                                            <FontAwesomeIcon icon={faIdCard} className="me-1 text-warning" />
                                            <strong>País Fact.:</strong> {cliente.vPaisFacturacion}

                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Col>

                    {/* Información de licencias */}
                    <Col xs={12} md={4} className="text-md-end">
                        <div className="mb-3">
                            <div className="text-muted small mb-2">
                                <strong className="text-success">Licencias</strong>
                            </div>

                            {/* Licencias Activas */}
                            <div className="mb-2 d-flex justify-content-end align-items-center">
                                <span className="small text-muted me-2">
                                    <strong>Activas:</strong>
                                </span>
                                <span className={`fw-bold ${cliente.dACL > 0 ? 'text-success' : 'text-secondary'}`} style={{ fontSize: '1.2rem' }}>
                                    {cliente.dACL}
                                </span>
                            </div>

                            {/* Licencias de Prueba */}
                            <div className="mb-2 d-flex justify-content-end align-items-center">
                                <span className="small text-muted me-2">
                                    <strong>Prueba:</strong>
                                </span>
                                <span className={`fw-bold ${cliente.dPPL > 0 ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.2rem' }}>
                                    {cliente.dPPL}
                                </span>
                            </div>

                          
                          
                            
                            {/* Total de Órdenes */}
                            {cliente.totalOrdenes !== undefined && cliente.totalOrdenes !== null && (
                                <>
                                    <hr className="my-2" />
                                    <div className="text-end">
                                        <div className="small text-muted mb-1">
                                            <strong>Total Órdenes:</strong>
                                        </div>
                                        <div className="fw-bold text-info" style={{ fontSize: '1.1rem' }}>
                                            ${cliente.totalOrdenes.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="small text-muted">MXN</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Ver detalles link */}
                        <div className="mt-3">
                            <Link to={url} className="btn btn-sm btn-outline-primary">
                                <img src="/img/ver20.png" alt="Ver" className="me-1" />
                                Ver detalles
                            </Link>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

ClienteCard.propTypes = {
    cliente: PropTypes.shape({
        idClienteAkasia: PropTypes.number.isRequired,
        vRfc: PropTypes.string,
        vRazonSocial: PropTypes.string,
        vNumRegTrib: PropTypes.string,
        bTieneLeadVinculado: PropTypes.number,
        dACL: PropTypes.number.isRequired,
        dPPL: PropTypes.number.isRequired,
        vNombre: PropTypes.string.isRequired,
        vEmail: PropTypes.string,
        vTelefonoPersonal: PropTypes.string,
        vPais: PropTypes.string.isRequired,
        vEstadoDepProv: PropTypes.string,
        vPaisFacturacion: PropTypes.string,
        totalOrdenes: PropTypes.number,
        joinDate: PropTypes.string.isRequired
    }).isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default ClienteCard;
